import {
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  useCreatePatientWeightMutation,
  useGetLatestWeightQuery,
} from "src/api/services/weightTrackerApi";
import { useTranslation } from "react-i18next";
import { heights } from "src/constants/heights";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import LoadingButton from "src/components/Buttons/LoadingButton";

const HeightWeight = () => {
  const [error, setError] = useState<string | null>(null);
  const [heightFt, setHeightFt] = useState<string>("");
  const [heightIn, setHeightIn] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const { data } = useGetLatestWeightQuery();
  const [createPatientWeight, { isLoading }] = useCreatePatientWeightMutation();
  const page = "patient-criteria-Q2";
  const { t } = useTranslation();
  const { submitAnswer } = useSubmitAnswers();

  const handleFeet = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setHeightFt(event.target.value);
  }, []);

  const handleInch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setHeightIn(event.target.value);
  }, []);

  const handleOnChangeWeight = useCallback(
    (weight: string) => {
      if (weight.length <= 3) {
        setWeight(weight);
      }
    },
    [setWeight]
  );

  const handleOnSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!heightFt || !heightIn || !weight) {
        setError("Please enter your height and weight");
        return;
      }

      const heightInInches = Number(heightFt) * 12 + Number(heightIn);

      try {
        await createPatientWeight({
          height: heightInInches,
          weight: Number(weight),
          date: new Date().toLocaleDateString("en-US"), //DO NOT change locale. this is how we store in db.
        }).unwrap();

        submitAnswer([JSON.stringify({ heightFt, heightIn, weight })]);
      } catch (error) {
        console.error("Error updating profile", error);
        setError("Failed to update profile");
      }
    },
    [heightFt, heightIn, weight, createPatientWeight, submitAnswer]
  );

  useEffect(() => {
    if (data != null) {
      setHeightFt(Math.floor(data.height / 12).toString());
      setHeightIn((data.height % 12).toString());
      setWeight(data.weight.toString());
    }
  }, [data]);

  return (
    <Stack gap={3} width="100%" component={"form"} onSubmit={handleOnSubmit}>
      <Stack gap={4}>
        <Stack direction="row" gap={2}>
          <TextField
            fullWidth
            select
            required
            label={t(`${page}.feet.label`)}
            value={heightFt}
            onChange={handleFeet}
          >
            {heights.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            required
            label={t(`${page}.inches.label`)}
            onChange={handleInch}
            value={heightIn}
          >
            {heights.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>
      <Stack gap={4}>
        <Typography variant="h5" fontWeight={700}>
          {t(`${page}.title2`)}
        </Typography>
        <FormControl>
          <TextField
            required
            type="number"
            label={t(`${page}.pounds.label`)}
            value={weight}
            onChange={(e) => handleOnChangeWeight(e.target.value)}
          />
        </FormControl>
      </Stack>
      {error ? (
        <Typography sx={{ color: "red" }} textAlign="center">
          {error}
        </Typography>
      ) : null}
      <LoadingButton type="submit" loading={isLoading}>
        {t("button.next", "Next")}
      </LoadingButton>
    </Stack>
  );
};

export default HeightWeight;
